import { RefObject, useCallback, useMemo, useRef, useState } from 'react';

import type { PageComponentProps } from './PaginatedDrawer';

/**
 * Manages pagination state for stepping through a list of pages, exposing the
 * shared `PageComponentProps` contract.
 */
export function usePaginatedPages<T>({
  pages: initialPages,
  initialPageIndex = 0,
  backButtonDisabled,
  onPageIndexChange,
}: {
  pages: T[];
  initialPageIndex?: number;
  backButtonDisabled?: boolean;
  onPageIndexChange?: (pageIndex: number) => void;
}): { componentProps: PageComponentProps<T>; contentRef: RefObject<HTMLDivElement> } {
  const [pages, setPages] = useState(initialPages);
  const [pageIndex, _setPageIndex] = useState<number>(initialPageIndex);

  const setPageIndex = useCallback(
    (index: number) => {
      _setPageIndex(index);
      onPageIndexChange?.(index);
    },
    [onPageIndexChange],
  );

  const isFirstPage = pageIndex === 0;
  const isLastPage = pageIndex === pages.length - 1;

  const contentRef = useRef<HTMLDivElement | null>(null);

  const componentProps = useMemo<PageComponentProps<T>>(
    () => ({
      page: pages[pageIndex],
      pageCount: pages.length,
      pageIndex,
      isFirstPage,
      isLastPage,
      nextPage: () => !isLastPage && setPageIndex(pageIndex + 1),
      prevPage: () => !isFirstPage && setPageIndex(pageIndex - 1),
      backButtonDisabled,
      setPage: (newPage: T | ((prevPage: T) => T)) => {
        setPages((prevPages) => {
          const updatedPages = [...prevPages];
          const updatedPage =
            // handle both forms of useState(value), useState(prev => value)
            typeof newPage === 'function' ? (newPage as (prevPage: T) => T)(updatedPages[pageIndex]) : newPage;
          updatedPages[pageIndex] = updatedPage;
          return updatedPages;
        });
      },
      scrollToTop: () => {
        if (contentRef.current) {
          contentRef.current.scrollTop = 0;
        }
      },
      scrollContainerRef: contentRef,
    }),
    [pages, pageIndex, setPageIndex, setPages, isFirstPage, isLastPage, backButtonDisabled],
  );

  return { componentProps, contentRef };
}
